import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import TypographyMask from '@/components/base/typography-mask'

import { heroParagraphRevealAnimation } from './animations'
import { WPLink } from '@/types'

export type HeroParagraphProps = HTMLAttributes<HTMLElement> &
  Partial<{
    overline: string
    title: string
    paragraph: string
    button: WPLink
  }>

const HeroParagraph = ({
  title,
  overline,
  paragraph,
  button,
  ...props
}: HeroParagraphProps) => {
  return (
    <Motion animation={heroParagraphRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Container>
            <Grid className="gap-y-[1.6rem] md:gap-y-[3.2rem]">
              {overline ? (
                <div className="col-span-4 text-center md:col-span-6 lg:col-span-8 lg:col-start-3">
                  <Typography
                    variant="h6"
                    component="h2"
                    className="font-timesnow"
                    data-motion-overline
                  >
                    {overline}
                  </Typography>
                </div>
              ) : null}

              {title ? (
                <div className="col-span-4 text-center md:col-span-6 lg:col-span-8 lg:col-start-3">
                  <Typography
                    variant="h1"
                    component="h1"
                    className="font-gillsans font-light uppercase"
                    data-motion-title
                  >
                    {title}
                  </Typography>
                </div>
              ) : null}

              {paragraph ? (
                <div className="col-span-4 text-center md:col-span-6 lg:col-span-8 lg:col-start-3">
                  <Typography
                    variant="body"
                    paragraph
                    className="font-gillsans font-light"
                    data-motion-paragraph
                  >
                    {paragraph}
                  </Typography>
                </div>
              ) : null}

              {button ? (
                <div className="col-span-4 md:col-span-6 lg:col-span-12">
                  <Button asChild data-motion-button>
                    <Link href={button.url} target={button.target}>
                      <TypographyMask>
                        <Typography variant="label">{button.title}</Typography>
                      </TypographyMask>
                    </Link>
                  </Button>
                </div>
              ) : null}
            </Grid>
          </Container>

          <div
            className="pointer-events-none fixed left-0 top-0 z-10 h-screen w-full bg-light"
            data-motion-mask
          />
        </Section>
      )}
    </Motion>
  )
}

export default HeroParagraph
